import React from 'react';

import { Formik, FormikValues } from 'formik';
import * as yup from 'yup';

import Header from 'components/AppBar';
import ApplicationOverlayContent from 'components/ApplicationLayout/ApplicationOverlayContent';
import Field from 'components/Field';
import Input from 'components/Input';
import ModalHeader from 'components/ModalHeader';
import { Desktop, Mobile } from 'components/Responsive';
import CustomSelect, { RenderProps as CustomSelectRenderProps } from 'components/Selects/CustomSelect';
import CitiesSearch from 'containers/CitiesSearch';
import CurrencyInput from 'containers/CurrencyInput';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import { City } from 'models/city';
import { SpecificDestination as SpecificDestinationModel } from 'models/specificDestination';

import { Scaffold, Container, About, Form, Fields, ButtonArea, ConfirmButton } from './styles';

interface DestinationForm {
  destinationPoint?: City;
  specificDestinationAnticipation?: number | '';
  maxPrice?: number | '';
}

interface Props {
  show: boolean;
  onClose: () => void;
  onConfirm: (data: SpecificDestinationModel) => void;
}

const SpecificDestination = ({ show, onConfirm, onClose }: Props) => {
  const toast = useToast();
  const { t } = useStoreTranslation();

  const initialValues: DestinationForm = {
    destinationPoint: undefined,
    specificDestinationAnticipation: undefined,
    maxPrice: 0,
  };

  const validationSchema = yup.object().shape({
    destinationPoint: yup.object().required(),
    specificDestinationAnticipation: yup.number().min(0),
    maxPrice: yup.number().min(0),
  });

  const handlePointSetValue = (city: City) => (city ? city.description : '');

  const handleSubmit = (data: DestinationForm) => {
    onConfirm({
      destinationPoint: {
        code: `${data.destinationPoint?.code}`,
        name: `${data.destinationPoint?.description}, ${data.destinationPoint?.country}`,
      },
      maxPrice: data.maxPrice ? data.maxPrice : '',
      dateHotel: data.specificDestinationAnticipation ? data.specificDestinationAnticipation : '',
    });
    onClose();
    toast.add({
      type: 'success',
      message: t('rule.specificDestination.addSuccess'),
    });
  };

  return (
    <>
      <ApplicationOverlayContent sidebar show={show} onClose={onClose}>
        <Scaffold>
          <Mobile>
            <Header goBack={onClose}>{t('rule.specificDestination.header')}</Header>
          </Mobile>

          <Desktop>
            <ModalHeader title={t('rule.specificDestination.header')} onClose={onClose} />
          </Desktop>

          <Container>
            <About>
              <About.Title>{t('rule.specificDestination.aboutTitle')}</About.Title>
              <About.Text>{t('rule.specificDestination.aboutText')}</About.Text>
            </About>
            <Formik
              initialValues={initialValues}
              isInitialValid={(formik: FormikValues) => validationSchema.isValidSync(formik.initialValues)}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
            >
              {({ isValid }) => (
                <Form>
                  <Fields>
                    <Field name="destinationPoint" label={t('rule.specificDestination.destinationPoint')}>
                      <CustomSelect
                        noIcon
                        placeHolder={t('rule.specificDestination.destinationPointPlaceHolder')}
                        onSetValue={handlePointSetValue}
                      >
                        {(selectProps: CustomSelectRenderProps) => (
                          <CitiesSearch
                            {...selectProps}
                            placeHolder={t('rule.specificDestination.destinationPointPlaceHolder')}
                          />
                        )}
                      </CustomSelect>
                    </Field>
                    <Field name="specificDestinationAnticipation" label={t('rule.restriction.anticipation')}>
                      <Input
                        type="number"
                        darkPlaceholder
                        suffix={t('rule.restriction.anticipationSuffix')}
                        placeholder={t('rule.restriction.withoutRestriction')}
                      />
                    </Field>
                    <Field name="maxPrice" label={t('rule.specificDestination.maxPrice')}>
                      <CurrencyInput placeHolder={t('rule.restriction.withoutRestriction')} />
                    </Field>
                  </Fields>
                  <ButtonArea>
                    <ConfirmButton disabled={!isValid} value={t('rule.specificDestination.confirm')} />
                  </ButtonArea>
                </Form>
              )}
            </Formik>
          </Container>
        </Scaffold>
      </ApplicationOverlayContent>
    </>
  );
};

export default SpecificDestination;
